import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./Dashboard.css";

const Dashboard = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [greeting, setGreeting] = useState("");
  const [inactivityTimer, setInactivityTimer] = useState(60);
  const [showWarning, setShowWarning] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  // References for timer
  const timerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const warningTimerRef = useRef(null);

  // Total session time in seconds
  const TOTAL_SESSION_TIME = 60;

  // Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }

    setInactivityTimer(TOTAL_SESSION_TIME);
    setShowWarning(false);

    // Show warning at 15 seconds remaining
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
    }, (TOTAL_SESSION_TIME - 15) * 1000);

    // Auto logout at 60 seconds
    logoutTimerRef.current = setTimeout(() => {
      handleAutoLogout();
    }, TOTAL_SESSION_TIME * 1000);
  }, []);

  // Handle auto logout
  const handleAutoLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    setIsAuthenticated(false);
    alert("You have been logged out due to inactivity (60 seconds).");
    navigate("/admin");
  };

  // User activity detection
  const handleUserActivity = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  // Handle profile click - navigate to home page
  const handleProfileClick = () => {
    resetInactivityTimer();
    // Navigate to the home page
    navigate("/");
  };

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin");
      return;
    }

    // Set greeting based on time
    const hour = currentTime.getHours();
    if (hour < 12) setGreeting("Good Morning");
    else if (hour < 18) setGreeting("Good Afternoon");
    else setGreeting("Good Evening");

    // Update time every minute
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    // Start inactivity timer
    resetInactivityTimer();

    // Update countdown every second
    const countdownTimer = setInterval(() => {
      setInactivityTimer((prev) => {
        if (prev <= 1) {
          return TOTAL_SESSION_TIME;
        }
        return prev - 1;
      });
    }, 1000);

    // Event listeners for user activity
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    events.forEach((event) => {
      document.addEventListener(event, handleUserActivity);
    });

    document.addEventListener("keydown", handleUserActivity);

    return () => {
      clearInterval(timer);
      clearInterval(countdownTimer);

      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }

      events.forEach((event) => {
        document.removeEventListener(event, handleUserActivity);
      });

      document.removeEventListener("keydown", handleUserActivity);
    };
  }, [navigate, currentTime, handleUserActivity, resetInactivityTimer]);

  const handleLogout = () => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }

    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    setIsAuthenticated(false);
    navigate("/admin");
  };

  const goToFirebaseStorage = () => {
    resetInactivityTimer();
    window.location.href =
      "https://console.firebase.google.com/project/photo-gallery-68f56/storage/photo-gallery-68f56.firebasestorage.app/files";
  };

  const handleQuickAction = (action) => {
    resetInactivityTimer();
    switch (action) {
      case "profile":
        handleProfileClick(); // Now navigates to home page
        break;
      case "settings":
        alert("System settings coming soon!");
        break;
      case "help":
        alert("Help documentation coming soon!");
        break;
      default:
        break;
    }
  };

  const extendSession = () => {
    resetInactivityTimer();
    alert(`Session extended for ${TOTAL_SESSION_TIME} seconds!`);
  };

  const adminUser = JSON.parse(localStorage.getItem("adminUser") || "{}");
  const loginTime = adminUser.loginTime
    ? new Date(adminUser.loginTime).toLocaleString()
    : "N/A";

  return (
    <div className="dashboard-container" onClick={handleUserActivity}>
      {/* Inactivity Warning Modal */}
      {showWarning && (
        <div className="inactivity-warning-overlay">
          <div className="inactivity-warning-modal">
            <div className="warning-header">
              <i className="fas fa-exclamation-triangle"></i>
              <h3>Session Timeout Warning</h3>
            </div>
            <div className="warning-body">
              <p>
                Your session will expire in <strong>{inactivityTimer}</strong>{" "}
                seconds due to inactivity.
              </p>
              <p>Click anywhere or press any key to continue your session.</p>
            </div>
            <div className="warning-footer">
              <button onClick={extendSession} className="extend-btn">
                <i className="fas fa-redo"></i> Extend Session
              </button>
              <button onClick={handleLogout} className="logout-now-btn">
                <i className="fas fa-sign-out-alt"></i> Logout Now
              </button>
            </div>
          </div>
        </div>
      )}

      <nav className="dashboard-nav">
        <div className="nav-left">
          <div className="nav-brand">
            <i className="fas fa-cloud"></i>
            <h2>FireBase Admin</h2>
          </div>
        </div>

        <div className="nav-right">
          <div className="session-timer">
            <i className="fas fa-clock"></i>
            <span>Session expires in: {inactivityTimer}s</span>
          </div>
          <div className="user-info">
            <div className="user-avatar">
              <i className="fas fa-user-circle"></i>
            </div>
            <div className="user-details">
              <span className="user-name">{adminUser.username || "Admin"}</span>
              <span className="user-role">Administrator</span>
            </div>
            <button onClick={handleLogout} className="logout-btn">
              <i className="fas fa-sign-out-alt"></i>
            </button>
          </div>
        </div>
      </nav>

      <main className="dashboard-main">
        <div className="dashboard-header">
          <div>
            <h1>
              {greeting}, {adminUser.username || "Admin"}!
            </h1>
            <p className="subtitle">
              Welcome to your Firebase management dashboard. Last login:{" "}
              {loginTime}
            </p>
          </div>
          <div className="time-display">
            <i className="fas fa-calendar-alt"></i>
            <span>{currentTime.toLocaleDateString()}</span>
            <i className="fas fa-clock"></i>
            <span>
              {currentTime.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </div>
        </div>

        <div className="dashboard-grid">
          {/* Stats Cards */}
          <div className="stats-grid">
            <div className="stat-card primary" onClick={goToFirebaseStorage}>
              <div className="stat-icon">
                <i className="fas fa-database"></i>
              </div>
              <div className="stat-content">
                <h3>Storage</h3>
                <p>Manage Firebase Files</p>
                <button className="stat-action">
                  Access Storage <i className="fas fa-arrow-right"></i>
                </button>
              </div>
            </div>

            <div
              className="stat-card success"
              onClick={() => handleQuickAction("settings")}
            >
              <div className="stat-icon">
                <i className="fas fa-cog"></i>
              </div>
              <div className="stat-content">
                <h3>Settings</h3>
                <p>System Configuration</p>
                <button className="stat-action">
                  Configure <i className="fas fa-arrow-right"></i>
                </button>
              </div>
            </div>

            <div
              className="stat-card warning"
              onClick={handleProfileClick}  // Now navigates to home page
            >
              <div className="stat-icon">
                <i className="fas fa-user-shield"></i>
              </div>
              <div className="stat-content">
                <h3>Profile</h3>
                <p>Account Settings</p>
                <button className="stat-action">
                  View Profile <i className="fas fa-arrow-right"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Session Info Card */}
          <div className="session-card">
            <div className="session-header">
              <h3>
                <i className="fas fa-user-clock"></i> Session Information
              </h3>
              <div className="session-status">
                <span className="status-dot active"></span>
                Active Session
              </div>
            </div>
            <div className="session-details">
              <div className="detail-item">
                <label>Auto-logout in:</label>
                <span className="countdown">{inactivityTimer} seconds</span>
              </div>
              <div className="detail-item">
                <label>Session Duration:</label>
                <span>{TOTAL_SESSION_TIME} seconds</span>
              </div>
              <div className="detail-item">
                <label>Login Time:</label>
                <span>{loginTime}</span>
              </div>
            </div>
            <div className="session-actions">
              <button onClick={extendSession} className="extend-session-btn">
                <i className="fas fa-redo"></i> Extend Session
              </button>
              <button onClick={handleLogout} className="end-session-btn">
                <i className="fas fa-sign-out-alt"></i> End Session
              </button>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="activity-card">
            <div className="activity-header">
              <h3>
                <i className="fas fa-history"></i> Recent Activity
              </h3>
              <span className="activity-badge">Today</span>
            </div>
            <div className="activity-list">
              <div className="activity-item">
                <div className="activity-icon success">
                  <i className="fas fa-sign-in-alt"></i>
                </div>
                <div className="activity-content">
                  <h4>Login Successful</h4>
                  <p>You logged into the admin dashboard</p>
                  <span className="activity-time">{loginTime}</span>
                </div>
              </div>
              <div className="activity-item">
                <div className="activity-icon info">
                  <i className="fas fa-shield-alt"></i>
                </div>
                <div className="activity-content">
                  <h4>Security Status</h4>
                  <p>Auto-logout enabled ({TOTAL_SESSION_TIME} seconds)</p>
                  <span className="activity-time">Session active</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
